import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Plus, ArrowUpRight, Calendar, User, Wand2, Mail, Network, Settings2, Clock, Columns, Globe, Sparkles, Upload as UploadIcon } from "lucide-react";
import { toast } from "sonner";
import BulkIndicatorImport from "../components/import/BulkIndicatorImport";

export default function Dashboard() {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [scope, setScope] = useState("");
  const [stats, setStats] = useState({}); // {pid: {rows_cleaned, minutes_saved}}
  const [prefs, setPrefs] = useState({ weekly_digest: false });
  const [me, setMe] = useState(null);
  // Two-step new-program flow: after create, optionally open the bulk
  // indicator importer so first-time users land productive on day one.
  const [bulkImportPid, setBulkImportPid] = useState(null);
  const [bulkPromptPid, setBulkPromptPid] = useState(null);
  // Step 3: after indicators are defined, offer to drop straight into
  // the upload wizard for a true zero-to-dashboard run.
  const [uploadPromptPid, setUploadPromptPid] = useState(null);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Phase 3.45 — auto-open the "Create program" dialog when the dashboard
  // empty-state CTA sends users here with `?new=1`. Strip the param after
  // opening so a manual close doesn't reopen on the next render.
  useEffect(() => {
    if (searchParams.get("new") === "1") {
      setOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete("new");
      setSearchParams(next, { replace: true });
    }
  }, []);

  const load = async () => {
    setLoading(true);
    const [{ data: progs }, { data: prefData }, { data: meData }] = await Promise.all([
      api.get("/programs"),
      api.get("/users/me/preferences"),
      api.get("/auth/me"),
    ]);
    setPrograms(progs);
    setPrefs({ weekly_digest: !!prefData.weekly_digest });
    setMe(meData);
    setLoading(false);
    // Fetch stats per program in parallel (best-effort; silently skip 403 viewer-only quirks)
    const statResults = await Promise.allSettled(
      progs.map((p) => api.get(`/programs/${p.id}/auto-clean-stats`).then((r) => [p.id, r.data]))
    );
    const next = {};
    for (const r of statResults) {
      if (r.status === "fulfilled") next[r.value[0]] = r.value[1];
    }
    setStats(next);
  };

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      const { data: created } = await api.post("/programs", { name, description, scope: scope || null });
      setName(""); setDescription(""); setScope(""); setOpen(false);
      toast.success("Program created");
      // Offer the bulk importer as step 2 — non-blocking, dismissible.
      setBulkPromptPid(created?.id || null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create program");
    }
  };

  const toggleDigest = async (next) => {
    setPrefs({ weekly_digest: next });
    try {
      await api.patch("/users/me/preferences", { weekly_digest: next });
      toast.success(next ? "You'll get a weekly impact digest each Sunday." : "Weekly digest turned off.");
    } catch (err) {
      setPrefs({ weekly_digest: !next });
      toast.error(err.response?.data?.detail || "Could not update preference");
    }
  };

  const totals = programs.reduce(
    (acc, p) => {
      const s = stats[p.id];
      if (!s) return acc;
      acc.rows += s.rows_cleaned || 0;
      acc.mins += s.minutes_saved || 0;
      return acc;
    },
    { rows: 0, mins: 0 }
  );

  return (
    <Layout>
      <div className="flex items-end justify-between gap-6 mb-8">
        <div>
          <p className="label-overline">Workspace</p>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tight font-semibold mt-2">Programs</h1>
          <p className="text-slate-600 mt-2 max-w-xl">A program organises indicators, uploaded data, and the live reports your team produces from it.</p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/compare">
            <Button variant="outline" data-testid="compare-programs-btn" disabled={programs.length < 2}>
              <Columns className="w-4 h-4 mr-1" /> Compare
            </Button>
          </Link>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-500 hover:bg-emerald-600 text-slate-900 font-medium" data-testid="new-program-button">
                <Plus className="w-4 h-4 mr-1" /> New program
              </Button>
            </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create a new program</DialogTitle>
              <DialogDescription>Group indicators, uploads and reports together for a single intervention or grant.</DialogDescription>
            </DialogHeader>
            <form onSubmit={create} className="space-y-4" data-testid="new-program-form">
              <div>
                <Label className="label-overline">Name</Label>
                <Input value={name} onChange={(e)=>setName(e.target.value)} required className="mt-2" placeholder="e.g. Health for All — 2025" data-testid="new-program-name-input" />
              </div>
              <div>
                <Label className="label-overline">Description</Label>
                <Textarea value={description} onChange={(e)=>setDescription(e.target.value)} className="mt-2" placeholder="Short summary of the program" data-testid="new-program-description-input" />
              </div>
              <div>
                <Label className="label-overline">Scope</Label>
                <select
                  value={scope}
                  onChange={(e) => setScope(e.target.value)}
                  className="mt-2 w-full border border-slate-200 rounded-sm bg-white text-sm px-2.5 py-2 focus:outline-none focus:border-slate-900"
                  data-testid="new-program-scope-input"
                >
                  <option value="">— Auto-detect from data —</option>
                  <option value="local">Local (community / city)</option>
                  <option value="national">National (multiple regions)</option>
                  <option value="regional">Regional (multiple countries)</option>
                  <option value="global">Global (3+ countries)</option>
                </select>
                <p className="text-[11px] text-slate-500 mt-1">Optional. Tells the dashboard whether to show city, regional, or country filters.</p>
              </div>
              <DialogFooter>
                <Button type="submit" className="bg-emerald-500 hover:bg-emerald-600 text-slate-900 font-medium" data-testid="new-program-submit-button">Create program</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Activity stats card */}
      {!loading && totals.rows > 0 && (
        <div
          className="bg-amber-50 border border-amber-200 rounded-sm p-4 mb-6 flex items-center gap-3"
          data-testid="auto-clean-activity-card"
        >
          <div className="w-9 h-9 bg-amber-100 rounded-sm grid place-items-center shrink-0">
            <Wand2 className="w-4 h-4 text-amber-700" />
          </div>
          <div className="flex-1 text-sm">
            <span className="font-medium text-amber-900">
              Cleaned <span className="font-mono">{totals.rows}</span> row{totals.rows === 1 ? "" : "s"}
            </span>
            <span className="text-amber-800"> · saved ~<span className="font-mono">{totals.mins}</span> min of manual work this week</span>
            <p className="text-[11px] text-amber-700 mt-0.5">
              Keep dirty rows flowing through Auto-Clean and your reports stay donor-ready without lifting a finger.
            </p>
          </div>
        </div>
      )}

      {/* User preferences card */}
      {!loading && me && (
        <div className="bg-white border border-slate-200 rounded-sm p-4 mb-8" data-testid="user-preferences-card">
          <div className="flex items-center gap-2 mb-3">
            <Settings2 className="w-4 h-4 text-slate-500" />
            <p className="label-overline">Your preferences</p>
          </div>
          <label className="flex items-start gap-3 cursor-pointer">
            <Switch
              checked={prefs.weekly_digest}
              onCheckedChange={toggleDigest}
              data-testid="weekly-digest-toggle"
            />
            <span className="flex-1 text-sm">
              <span className="text-slate-800 font-medium flex items-center gap-2">
                <Mail className="w-3.5 h-3.5 text-slate-500" /> Weekly impact digest
              </span>
              <span className="text-xs text-slate-500 mt-0.5 block">
                Every Sunday morning we'll email you a 1-page summary: top indicators, % to target, dirty-data debt, and AI captions.
              </span>
            </span>
          </label>
        </div>
      )}

      {loading ? (
        <div className="text-slate-500 text-sm" data-testid="programs-loading">Loading programs…</div>
      ) : programs.length === 0 ? (
        <div
          className="border border-dashed border-emerald-300 bg-emerald-50/40 p-12 text-center rounded-sm"
          data-testid="programs-empty"
        >
          <div className="w-14 h-14 rounded-full bg-emerald-100 grid place-items-center mx-auto">
            <Sparkles className="w-7 h-7 text-emerald-600" />
          </div>
          <p className="label-overline mt-4">No programs yet</p>
          <h3 className="font-heading text-2xl mt-2">Start by adding a program and indicators</h3>
          <p className="text-slate-600 mt-2 max-w-md mx-auto text-sm">
            Programs are the home for your indicators, uploaded data, and the live reports you&apos;ll share with donors.
            It takes about 30 seconds to set one up — we&apos;ll walk you through indicators and your first upload right after.
          </p>
          <ul className="mt-5 inline-flex flex-col gap-1.5 text-left text-xs text-slate-600 bg-white border border-slate-200 rounded-sm p-4">
            <li className="flex items-center gap-2"><span className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 grid place-items-center text-[10px] font-semibold">1</span>Create your first program</li>
            <li className="flex items-center gap-2"><span className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 grid place-items-center text-[10px] font-semibold">2</span>Add indicators (use bulk AI import for speed)</li>
            <li className="flex items-center gap-2"><span className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 grid place-items-center text-[10px] font-semibold">3</span>Upload your first dataset</li>
          </ul>
          <div className="mt-6">
            <Button
              onClick={() => setOpen(true)}
              className="bg-emerald-500 hover:bg-emerald-600 text-slate-900 font-medium"
              data-testid="programs-empty-cta"
            >
              <Plus className="w-4 h-4 mr-1" /> Create your first program
            </Button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="programs-grid">
          {programs.map((p) => (
            <ProgramCard key={p.id} program={p} stat={stats[p.id]} onChanged={load} />
          ))}
        </div>
      )}

      {bulkPromptPid && (
        <Dialog open onOpenChange={(o) => { if (!o) setBulkPromptPid(null); }}>
          <DialogContent className="max-w-md" data-testid="bulk-prompt-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-emerald-600" /> Add your indicators
              </DialogTitle>
              <DialogDescription>
                Program created. Bulk-import your indicators now to land productive on day one — or skip and add them later.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2 mt-2">
              <Button
                onClick={() => { setBulkImportPid(bulkPromptPid); setBulkPromptPid(null); }}
                className="w-full bg-emerald-500 hover:bg-emerald-600 text-slate-900 font-medium"
                data-testid="bulk-prompt-yes"
              >
                <Sparkles className="w-4 h-4 mr-1.5" /> Bulk import indicators
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  // Skip indicator import but still offer the upload step —
                  // some users already have indicators from a previous
                  // program or will add them later, but want to upload now.
                  const pid = bulkPromptPid;
                  setBulkPromptPid(null);
                  setUploadPromptPid(pid);
                }}
                className="w-full"
                data-testid="bulk-prompt-skip"
              >
                I'll add them manually later
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
      {bulkImportPid && (
        <BulkIndicatorImport
          pid={bulkImportPid}
          open={!!bulkImportPid}
          onClose={() => setBulkImportPid(null)}
          onImported={() => {
            // After indicators are in, chain into step 3 so the user
            // can upload real data without leaving the wizard flow.
            const pid = bulkImportPid;
            setBulkImportPid(null);
            setUploadPromptPid(pid);
            load();
          }}
        />
      )}
      {uploadPromptPid && (
        <Dialog open onOpenChange={(o) => { if (!o) setUploadPromptPid(null); }}>
          <DialogContent className="max-w-md" data-testid="upload-prompt-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UploadIcon className="w-5 h-5 text-emerald-600" /> Upload your first dataset
              </DialogTitle>
              <DialogDescription>
                Indicators are ready. Upload data now to see your live dashboard in under a minute — or skip and upload from the program page when you're ready.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2 mt-2">
              <Button
                onClick={() => {
                  const pid = uploadPromptPid;
                  setUploadPromptPid(null);
                  navigate(`/programs/${pid}/upload`);
                }}
                className="w-full bg-emerald-500 hover:bg-emerald-600 text-slate-900 font-medium"
                data-testid="upload-prompt-yes"
              >
                <UploadIcon className="w-4 h-4 mr-1.5" /> Open upload wizard
              </Button>
              <Button
                variant="outline"
                onClick={() => setUploadPromptPid(null)}
                className="w-full"
                data-testid="upload-prompt-skip"
              >
                I'll upload data later
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </Layout>
  );
}

function ProgramCard({ program: p, stat, onChanged }) {
  const [showLog, setShowLog] = useState(!!p.show_logframe_public);
  const [busy, setBusy] = useState(false);

  const toggle = async (next) => {
    setBusy(true);
    setShowLog(next);
    try {
      await api.patch(`/programs/${p.id}`, { show_logframe_public: next });
      toast.success(next ? "LogFrame thumbnail will appear on the public report." : "LogFrame thumbnail hidden from donors.");
      onChanged?.();
    } catch (err) {
      setShowLog(!next);
      toast.error(err.response?.data?.detail || "Could not update — admin role required.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="group bg-white border border-slate-200 hover:border-slate-900 rounded-sm p-6 flex flex-col justify-between transition-colors"
      data-testid={`program-card-${p.id}`}
    >
      <Link to={`/programs/${p.id}`} className="block">
        <div className="flex items-start justify-between">
          <p className="label-overline">Program</p>
          <ArrowUpRight className="w-4 h-4 text-slate-400 group-hover:text-slate-900" />
        </div>
        <h3 className="font-heading text-xl font-semibold mt-2 leading-snug">{p.name}</h3>
        <p className="text-sm text-slate-600 mt-2 line-clamp-3">{p.description || "No description provided."}</p>
      </Link>
      <ScopePicker program={p} onChanged={onChanged} />
      {stat && stat.rows_cleaned > 0 && (
        <p className="mt-3 text-[11px] text-amber-800 flex items-center gap-1" data-testid={`program-card-stat-${p.id}`}>
          <Clock className="w-3 h-3" /> Auto-cleaned {stat.rows_cleaned} rows · saved ~{stat.minutes_saved} min this week
        </p>
      )}
      <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between gap-3">
        <div className="text-xs text-slate-500 flex items-center gap-3">
          <span className="flex items-center gap-1"><User className="w-3 h-3" /> {p.owner_name}</span>
          <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {new Date(p.created_at).toLocaleDateString()}</span>
        </div>
        <label
          className="flex items-center gap-2 text-[11px] text-slate-600 cursor-pointer shrink-0"
          title="Show a small LogFrame diagram on the public donor report"
          onClick={(e) => e.stopPropagation()}
        >
          <Network className="w-3 h-3 text-slate-400" />
          <span className="hidden sm:inline">LogFrame</span>
          <Switch
            checked={showLog}
            onCheckedChange={toggle}
            disabled={busy}
            data-testid={`logframe-toggle-${p.id}`}
          />
        </label>
      </div>
    </div>
  );
}

function ScopePicker({ program, onChanged }) {
  const [val, setVal] = useState(program.scope || "");
  const [busy, setBusy] = useState(false);
  const change = async (next) => {
    setBusy(true);
    const prev = val;
    setVal(next);
    try {
      await api.patch(`/programs/${program.id}`, { scope: next || null });
      toast.success(next ? `Scope set to ${next}.` : "Scope cleared — will auto-detect from data.");
      onChanged?.();
    } catch (err) {
      setVal(prev);
      toast.error(err.response?.data?.detail || "Could not update scope.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-3 flex items-center gap-2 text-[11px]" onClick={(e) => e.stopPropagation()}>
      <Globe className="w-3 h-3 text-slate-400" />
      <span className="text-slate-500">Scope</span>
      <select
        value={val}
        onChange={(e) => change(e.target.value)}
        disabled={busy}
        className="border border-slate-200 rounded-sm bg-white px-1.5 py-0.5 text-[11px] focus:outline-none focus:border-slate-900"
        data-testid={`program-scope-${program.id}`}
      >
        <option value="">Auto-detect</option>
        <option value="local">Local</option>
        <option value="national">National</option>
        <option value="regional">Regional</option>
        <option value="global">Global</option>
      </select>
    </div>
  );
}

